
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function GeorgePage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "George Painters - Professional Painting Services Garden Route";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for George properties in the Garden Route. Expert interior, exterior, and commercial painting for homes and businesses. Get your free quote today.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'George painters, Garden Route painting, commercial painting George, residential painting, holiday home painting, Western Cape painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/george`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "George Painters - Professional Painting Services",
      "description": "Professional painting services for George properties in the Garden Route. Expert interior, exterior, and commercial painting for homes and businesses.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/george`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - George",
        "description": "Comprehensive painting services for George's residential and commercial properties in the Garden Route.",
        "areaServed": "George, Garden Route, Western Cape",
        "serviceType": "Garden Route Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('George')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Residential Home Painting',
      description: 'Complete interior and exterior painting services for George homes including family residences, holiday homes, and retirement properties.',
      price: 'From R12,000',
      features: ['Weather-resistant coatings', 'Holiday home maintenance', 'Retirement-friendly service', 'Quality finishes']
    },
    {
      icon: 'ri-building-2-line',
      title: 'Commercial Property Painting',
      description: 'Professional painting services for George businesses, offices, retail spaces, and hospitality establishments in the Garden Route.',
      price: 'From R20,000',
      features: ['Business scheduling', 'Professional appearance', 'Hospitality standards', 'Minimal disruption']
    },
    {
      icon: 'ri-hotel-line',
      title: 'Holiday Accommodation Painting',
      description: 'Specialized painting services for George\'s tourism industry including guesthouses, B&Bs, and vacation rentals.',
      price: 'From R15,000',
      features: ['Tourism standards', 'Quick turnaround', 'Guest-friendly colors', 'Seasonal scheduling']
    },
    {
      icon: 'ri-landscape-line',
      title: 'Garden Route Climate Protection',
      description: 'Weather-resistant painting solutions designed for George\'s unique Garden Route climate including humidity and coastal influences.',
      price: 'From R10,000',
      features: ['Climate-specific coatings', 'Humidity resistance', 'UV protection', 'Long-term durability']
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Consultation Services',
      description: 'Expert color consultation for George properties considering the natural Garden Route environment and local architectural styles.',
      price: 'From R2,000',
      features: ['Garden Route aesthetics', 'Natural color palettes', 'Environmental harmony', 'Professional advice']
    },
    {
      icon: 'ri-tools-fill',
      title: 'Maintenance Programs',
      description: 'Ongoing maintenance programs for George properties including regular inspections and preventive treatments for the Garden Route climate.',
      price: 'From R5,000',
      features: ['Climate monitoring', 'Preventive maintenance', 'Regular inspections', 'Cost-effective solutions']
    }
  ];

  const testimonials = [
    {
      name: 'Robert Anderson',
      property: 'George Family Home',
      rating: 5,
      text: 'Excellent service for our home in George. The team understood the local climate challenges and provided solutions that have lasted beautifully through all seasons.',
      image: 'https://public.readdy.ai/ai/img_res/7d9339579c9ed6dabed6acb753ca8ac5.jpg'
    },
    {
      name: 'Helen Matthews',
      property: 'George Guesthouse',
      rating: 5,
      text: 'Outstanding work on our guesthouse. They worked efficiently around our guests and delivered a beautiful finish that enhances our property\'s appeal to visitors.',
      image: 'https://readdy.ai/api/search-image?query=Business%20woman%20owner%20of%20George%20guesthouse%20with%20Garden%20Route%20setting%2C%20professionally%20painted%20accommodation%2C%20satisfied%20hospitality%20owner&width=80&height=80&seq=george2&orientation=squarish'
    },
    {
      name: 'David Kruger',
      property: 'George Retirement Home',
      rating: 5,
      text: 'Professional and considerate service perfect for our retirement community. The quality is excellent and they were very respectful of our residents\' needs.',
      image: 'https://readdy.ai/api/search-image?query=Elderly%20gentleman%20in%20George%20retirement%20home%20with%20Garden%20Route%20views%2C%20freshly%20painted%20walls%2C%20comfortable%20living%20environment%2C%20satisfied%20resident&width=80&height=80&seq=george3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Garden Route Guesthouse',
      description: 'Complete interior and exterior painting of luxury guesthouse with tourism-grade finishes and weather-resistant coatings',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20guesthouse%20in%20George%20Garden%20Route%20with%20mountain%20backdrop%2C%20professionally%20painted%20exterior%2C%20hospitality%20accommodation%2C%20tourism%20property&width=400&height=300&seq=george4&orientation=landscape'
    },
    {
      title: 'George Business District Office',
      description: 'Professional commercial painting project for modern office building in George\'s central business district',
      image: 'https://readdy.ai/api/search-image?query=Modern%20office%20building%20in%20George%20business%20district%20with%20professionally%20painted%20exterior%2C%20commercial%20architecture%2C%20Garden%20Route%20setting&width=400&height=300&seq=george5&orientation=landscape'
    },
    {
      title: 'Family Holiday Home',
      description: 'Interior and exterior painting of family holiday home with climate-resistant materials and vacation-friendly colors',
      image: 'https://readdy.ai/api/search-image?query=Family%20holiday%20home%20in%20George%20with%20Garden%20Route%20mountain%20views%2C%20freshly%20painted%20exterior%2C%20vacation%20property%2C%20residential%20architecture&width=400&height=300&seq=george6&orientation=landscape'
    },
    {
      title: 'Retirement Village Complex',
      description: 'Comprehensive painting project for retirement village including residential units and common areas with senior-friendly considerations',
      image: 'https://readdy.ai/api/search-image?query=Retirement%20village%20complex%20in%20George%20with%20Garden%20Route%20setting%2C%20professionally%20painted%20buildings%2C%20senior%20living%20community%2C%20peaceful%20environment&width=400&height=300&seq=george7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20property%20in%20George%20Garden%20Route%20with%20Outeniqua%20Mountains%20backdrop%2C%20painting%20equipment%2C%20scenic%20Garden%20Route%20setting&width=1920&height=1080&seq=georgehero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters George
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for George properties in the beautiful Garden Route. Specialized solutions for residential homes, commercial buildings, and hospitality establishments with climate-resistant materials designed for the Garden Route environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About George Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Painting Excellence in George
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                George is the heart of the Garden Route, known for its stunning natural beauty, thriving tourism industry, and growing residential communities. Our painting services are designed to meet the diverse needs of this dynamic city, from family homes and retirement villages to commercial properties and hospitality establishments.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Located between the Outeniqua Mountains and the Indian Ocean, George experiences a unique climate that requires specialized painting solutions. We understand the local environmental challenges including humidity, seasonal weather variations, and the need for durable finishes that can withstand the Garden Route's diverse conditions.
              </p>
              <p className="text-lg text-gray-600">
                Our experience with George's varied property types, from modern developments to established neighborhoods and commercial districts, ensures we deliver results that enhance your property's value while providing long-lasting protection in this beautiful Garden Route setting.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=George%20city%20overview%20with%20Outeniqua%20Mountains%2C%20Garden%20Route%20setting%2C%20residential%20and%20commercial%20properties%2C%20natural%20beauty%2C%20Western%20Cape%20landscape&width=600&height=400&seq=george8&orientation=landscape"
                alt="George Garden Route"
                className="w-full h-96 object-cover object-center rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive George Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed for George's diverse properties and Garden Route climate conditions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent George Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in Garden Route painting with recent successful projects in George.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-center"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              George Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied George property owners who have experienced our exceptional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your George Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact the Garden Route's premier painting specialists for George. Our expert team is ready to provide you with comprehensive painting solutions designed for the unique Garden Route environment.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
          </div>
          <a
            href="mailto:Get a quote"
            className="bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Quote
          </a>
        </div>
      </section>
      <Footer />
    </div>
  );
}
